"use client";

import React, { useLayoutEffect, useRef } from "react";
import Image from "next/image";
import Images from "../assets/assets";
// PERF: GSAP + ScrollTrigger loaded lazily via ensureGsap() (this is a
// below-the-fold pinned scroll animation, so GSAP loads well before the user
// scrolls to it — no flash/CLS). registerPlugin now runs after mount.
import { ensureGsap } from "@/lib/EnsureGsap";
import { StaticImageData } from "next/image";

interface BenefitItem {
  title: string;
  description: string;
  icons: string | StaticImageData;
}

interface WhyKonstantProps {
  title: React.ReactNode;
  description: string;
  subtitle: string;
  benefitsArray: BenefitItem[];
}

const WhyKonstantAi = ({
  title,
  description,
  subtitle,
  benefitsArray,
}: WhyKonstantProps) => {
  const componentRef = useRef<HTMLElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<HTMLElement[]>([]);
  const bulletRefs = useRef<HTMLButtonElement[]>([]);
  const lastActiveRef = useRef<number>(0);

  const StackCard: React.FC<{ item: BenefitItem }> = ({ item }) => {
    return (
      <article
        className="lg:absolute inset-0 overflow-hidden border border-white/30 backdrop-blur-[2px] bg-[#111]"
        aria-roledescription="card"
        style={{
          backgroundImage: `url(${Images?.ai_card_bg})`,
          backgroundSize: "100% 100%",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-[20px] lg:gap-[4%] px-[5%] py-[6%] h-full text-center sm:text-left">
          <div className="shrink-0 w-[54px] h-[54px] lg:w-[clamp(60px,5vw,80px)] lg:h-[clamp(60px,5vw,80px)] rounded-[14px] grid place-items-center border border-white/20 bg-white/5">
            <Image
              src={item?.icons || ""}
              alt="AI icon"
              width={36}
              height={36}
              className="w-[50%] h-[50%] object-contain"
            />
          </div>
          <div className="flex flex-col gap-[10px] lg:gap-[15px] w-full">
            <h3 className="text-white text-[clamp(1.5rem,2.2vw,2.8rem)] font-medium clash-font leading-[120%]">
              {item.title}
            </h3>
            <p className="text-[#949596] text-[clamp(0.95rem,1.1vw,1.3rem)] leading-[150%] max-w-[90%]">
              {item.description}
            </p>
          </div>
        </div>
      </article>
    );
  };

  useLayoutEffect(() => {
    const isMobile = window.innerWidth < 1024;
    if (isMobile || !componentRef.current || !stageRef.current) return;

    const cards = cardRefs.current.filter(Boolean);
    if (cards.length === 0) return;

    let cleanup: (() => void) | null = null;
    let cancelled = false;

    ensureGsap().then(({ gsap, ScrollTrigger }) => {
      if (cancelled) return;

      const spacing = 40;
      const scaleStep = 0.05;
      const VISIBLE_DEPTH = 4;
      const EXIT_Y = 150;

      let ctx = gsap.context(() => {
        const updateLayout = (progress: number) => {
          const activeIndexFloat = progress * (cards.length - 1);

          cards.forEach((card, i) => {
            const rel = i - activeIndexFloat;

            if (rel <= -1) {
              gsap.set(card, { opacity: 0.01, y: EXIT_Y, scale: 1, zIndex: 0 });
            } else if (rel < 0) {
              const exitProgress = Math.abs(rel);
              gsap.set(card, {
                y: exitProgress * EXIT_Y,
                opacity: 1 - exitProgress,
                scale: 1,
                zIndex: 100,
              });
            } else {
              const yOffset = -rel * spacing;
              const scaleVal = Math.max(1 - rel * scaleStep, 0.85);
              const zIndexVal = Math.floor(50 - rel);
              const opacityVal = rel >= VISIBLE_DEPTH ? 0 : 1;

              gsap.set(card, {
                y: yOffset,
                scale: scaleVal,
                opacity: opacityVal,
                zIndex: zIndexVal,
              });
            }
          });

          const currentIdx = Math.round(activeIndexFloat);
          if (lastActiveRef.current !== currentIdx) {
            lastActiveRef.current = currentIdx;
            bulletRefs.current.forEach((bullet, i) => {
              if (bullet) {
                bullet.setAttribute(
                  "aria-current",
                  i === currentIdx ? "true" : "false",
                );
              }
            });
          }
        };

        updateLayout(0);

        ScrollTrigger.create({
          trigger: componentRef.current,
          start: "top top",
          end: `+=${cards.length * 1000}`,
          scrub: 0.5,
          pin: true,
          pinSpacing: true,
          anticipatePin: 1,
          // This is the TOPMOST of three lazy-loaded pinned sections on the page
          // (this → IndustryProcess → IndustryApproach). They register their pins
          // asynchronously and out of order, so ScrollTrigger can measure a lower
          // section BEFORE this one's huge pin-spacer (~cards.length*1000px)
          // exists — caching its start thousands of px off and leaving a
          // full-screen white gap. A higher refreshPriority forces this trigger to
          // recompute FIRST so its spacer is in the DOM before the sections below
          // measure. Chain: this (1) > IndustryProcess (0) > IndustryApproach (-1).
          refreshPriority: 1,
          invalidateOnRefresh: true,
          onUpdate: (self) => updateLayout(self.progress),
        });
      });

      cleanup = () => ctx.revert();
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [benefitsArray]);

  return (
    // Wrapper is React-owned; the pinned section sits INSIDE it so GSAP's
    // injected pin-spacer never displaces this component's root in the page
    // container's child list — otherwise a sibling section swapping its tree
    // makes React insertBefore against a node GSAP re-parented and throw
    // `NotFoundError`. See components/common/Solution/IndustryProcess.tsx.
    <div className="w-full">
      <section
        ref={componentRef}
        className="relative w-full min-h-screen flex items-center overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `url(${Images?.ai_container_bg})` }}
      >
        <div className="w-full px-[5%] mx-auto max-w-[2500px] py-10 sm:py-0">
          <div className="flex flex-col gap-[40px] lg:flex-row lg:items-center lg:justify-between">
            {/* Left Content */}
            <div className="w-full lg:w-[42%] flex flex-col gap-[20px] lg:gap-[30px] pt-8 lg:pt-0">
              <span className="text-white text-[16px] font-medium uppercase tracking-wider">
                {subtitle}
              </span>
              <h2 className="text-white text-[length:var(--section-title)] font-medium clash-font leading-[120%]">
                {title}
              </h2>
              <p className="text-[#949596] text-[clamp(1rem,1.2vw,1.4rem)] font-normal leading-[160%]">
                {description}
              </p>
            </div>

            {/* Right Side - Cards */}
            <div className="w-full lg:w-[53%] flex justify-center items-center">
              {/* Mobile View */}
              <div className="lg:hidden w-full flex flex-col gap-[40px] lg:gap-[20px] py-10">
                {benefitsArray?.map((item, i) => (
                  <div
                    key={i}
                    className="shadow-2xl overflow-visible md:overflow-hidden aspect-auto lg:aspect-[16/9]"
                  >
                    <StackCard item={item} />
                  </div>
                ))}
              </div>

              {/* Desktop View */}
              <div className="hidden lg:block w-full max-w-[950px] relative">
                <div
                  ref={stageRef}
                  /* Use aspect ratio to ensure cards grow with width on big screens but stay proportional */
                  className="relative w-full aspect-[16/7] min-h-[300px] [perspective:1200px] [transform-style:preserve-3d] isolation-isolate"
                >
                  {benefitsArray?.map((item, i) => (
                    <div
                      key={i}
                      ref={(el) => {
                        if (el) cardRefs.current[i] = el;
                      }}
                      className="absolute inset-0 shadow-2xl overflow-hidden"
                    >
                      <StackCard item={item} />
                    </div>
                  ))}

                  {/* Navigation Dots */}
                  <nav className="absolute right-[-8%] top-1/2 -translate-y-1/2 flex items-center justify-center">
                    <ul className="flex flex-col gap-3">
                      {benefitsArray?.map((_, i) => (
                        <li key={i}>
                          <button
                            ref={(el) => {
                              if (el) bulletRefs.current[i] = el;
                            }}
                            type="button"
                            aria-label={`View benefit ${i + 1}`}
                            aria-current={i === 0 ? "true" : "false"}
                            className="w-3 h-3 rounded-full bg-[#515765]/60 transition-all duration-300 hover:bg-white hover:scale-125 aria-[current=true]:bg-white aria-[current=true]:scale-125"
                          />
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyKonstantAi;
